import { Injectable, inject } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { firstValueFrom } from "rxjs";

import { environment } from "../../../environments/environment";
import { AuthStateService } from "../services/auth-state.service";

import type { IAuthProvider } from "./interfaces";

/**
 * Microsoft Entra provider for Davaco employees on the apex host.
 *
 * Entra is the ONLY way a Davaco employee signs in — there is no social or
 * password path to a `Davaco - Admin` session.
 */
@Injectable({ providedIn: "root" })
export class EntraAuthService implements IAuthProvider {

  private readonly msal = inject(MsalService);

  private readonly authState = inject(AuthStateService);

  private account(): AccountInfo | null {
    return this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? null;
  }

  public async initialize(): Promise<void> {
    await this.msal.instance.initialize();
    await firstValueFrom(this.msal.handleRedirectObservable());

    const account: AccountInfo | null = this.account();
    if (account === null) {
      this.authState.clear();
      return;
    }

    this.msal.instance.setActiveAccount(account);
    this.authState.setFromClaims((account.idTokenClaims ?? {}) as Record<string, unknown>);
  }

  public async login(): Promise<void> {
    await this.msal.instance.loginRedirect({ scopes: environment.scopes.loginRequest });
  }

  public async logout(): Promise<void> {
    this.authState.clear();
    await this.msal.instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  public async getAccessToken(): Promise<string | null> {
    return this.acquire(false);
  }

  /**
   * A staff token never carries a customer claim, so the stale-claim 401 this
   * exists for cannot happen to an employee. Implemented anyway rather than
   * returning null: the interceptor is provider-agnostic, and a provider that
   * silently refused to refresh would turn any future 401-retry path into a
   * dead end for staff only — the kind of asymmetry nobody finds until it
   * matters.
   */
  public async refreshAccessToken(): Promise<string | null> {
    return this.acquire(true);
  }

  private async acquire(forceRefresh: boolean): Promise<string | null> {
    const account: AccountInfo | null = this.account();
    if (account === null) {
      return null;
    }

    try {
      const result = await this.msal.instance.acquireTokenSilent({
        account,
        scopes: environment.apiConfig.scopes,
        forceRefresh,
      });
      return result.accessToken;
    } catch (error: unknown) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.msal.instance.acquireTokenRedirect({ scopes: environment.apiConfig.scopes });
      }
      return null;
    }
  }
}
